import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import GlassButton from './GlassButton';
import './fonts.css';
import { supabase } from './supabaseClient';

function AmalAdmin() {
  const [state, setState] = useState({
    amals: [],
    loading: false,
    error: null,
    editingAmal: null,
    editingValues: {}, // For storing temporary values during editing
    incrementInputs: {}
  });

  const normalizeAmals = (list) => {
    const requiredDefaults = [
      { id: 1, name: 'نماز جعفر طیار', count: 0, order_num: 1 },
      { id: 2, name: 'نماز شب', count: 0, order_num: 2 },
      { id: 3, name: 'نماز استغفار', count: 0, order_num: 3 },
      { id: 4, name: 'صفحه قرآن', count: 0, order_num: 4 }
    ];

    const arr = Array.isArray(list) ? list : [];
    const normalized = arr
      .map((a, idx) => {
        const idNum = parseInt(a?.id);
        const countNum = parseInt(a?.count);
        const orderNum = parseInt(a?.order_num);
        return {
          ...a,
          id: Number.isFinite(idNum) ? idNum : a?.id,
          name: typeof a?.name === 'string' ? a.name : String(a?.name || ''),
          count: Number.isFinite(countNum) ? Math.max(0, countNum) : 0,
          order_num: Number.isFinite(orderNum) ? orderNum : (idx + 1)
        };
      })
      .sort((x, y) => (x.order_num || 0) - (y.order_num || 0));

    const byId = new Map(normalized.map(a => [a.id, a]));
    requiredDefaults.forEach((d) => {
      if (!byId.has(d.id)) {
        byId.set(d.id, d);
      }
    });

    return Array.from(byId.values()).sort((x, y) => (x.order_num || 0) - (y.order_num || 0));
  };

  const setLoading = (loading) => setState(prev => ({ ...prev, loading }));
  const setError = (error) => setState(prev => ({ ...prev, error }));
  const setAmals = (amals) => setState(prev => ({ ...prev, amals }));
  const setEditingAmal = (editingAmal) => {
  setState(prev => ({ 
    ...prev, 
    editingAmal,
    editingValues: editingAmal ? {
      ...prev.editingValues,
      [editingAmal]: prev.amals.find(a => a.id === editingAmal)?.count || 0
    } : {}
  }));
};

  const updateEditingValue = (id, value) => {
    setState(prev => ({
      ...prev,
      editingValues: {
        ...prev.editingValues,
        [id]: value
      }
    }));
  };

  const handleBulkIncrementAll = (direction) => {
    const amalsList = Array.isArray(state.amals) ? state.amals : [];
    if (amalsList.length === 0) return;

    const rows = amalsList.map((a) => {
      const raw = (state.incrementInputs && state.incrementInputs[a.id] !== undefined) ? state.incrementInputs[a.id] : 0;
      const delta = Math.abs(parseInt(raw) || 0);
      return { id: a.id, delta: direction * delta };
    });

    const hasAny = rows.some(r => r.delta !== 0);
    if (!hasAny) return;

    const updatedAmals = amalsList.map((a) => {
      const row = rows.find(r => r.id === a.id);
      const base = parseInt(a.count) || 0;
      return {
        ...a,
        count: Math.max(0, base + (row?.delta || 0))
      };
    });

    saveAmals(updatedAmals);
    setState(prev => ({ ...prev, incrementInputs: {} }));
  };

  const saveAmalEdit = (id) => {
    const newCount = state.editingValues[id];
    if (newCount >= 0 && newCount <= 999999) {
      updateAmal(id, { count: newCount });
    }
  };

  // Fetch amals from database or local storage
  useEffect(() => {
    fetchAmals();
  }, []);

  const fetchAmals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch from Supabase first
      const { data, error } = await supabase
        .from('amals')
        .select('*')
        .order('order_num', { ascending: true });

      if (error) {
        console.log('Supabase error, falling back to localStorage:', error);
        // Fallback to localStorage if Supabase fails
        const savedAmals = localStorage.getItem('amals');
        if (savedAmals) {
          try {
            const parsed = JSON.parse(savedAmals);
            const normalized = normalizeAmals(parsed);
            setAmals(normalized);
          } catch (e) {
            localStorage.removeItem('amals');
          }
        } else {
          // Default amals
          const defaultAmals = [
            { id: 1, name: 'نماز جعفر طیار', count: 0, order_num: 1 },
            { id: 2, name: 'نماز شب', count: 0, order_num: 2 },
            { id: 3, name: 'نماز استغفار', count: 0, order_num: 3 }
          ];
          const normalizedDefaults = normalizeAmals(defaultAmals);
          setAmals(normalizedDefaults);
          localStorage.setItem('amals', JSON.stringify(normalizedDefaults));
        }
      } else {
        const next = normalizeAmals(data || []);

        if (next.length === 0) {
          const savedAmals = localStorage.getItem('amals');
          if (savedAmals) {
            try {
              const parsed = JSON.parse(savedAmals);
              const normalized = normalizeAmals(parsed);
              if (normalized.length > 0) {
                setAmals(normalized);
                return;
              }
              localStorage.removeItem('amals');
            } catch (e) {
              localStorage.removeItem('amals');
            }
          } else {
            const defaultAmals = [
              { id: 1, name: 'نماز جعفر طیار', count: 0, order_num: 1 },
              { id: 2, name: 'نماز شب', count: 0, order_num: 2 },
              { id: 3, name: 'نماز استغفار', count: 0, order_num: 3 }
            ];
            const normalizedDefaults = normalizeAmals(defaultAmals);
            setAmals(normalizedDefaults);
            localStorage.setItem('amals', JSON.stringify(normalizedDefaults));
          }
          const defaultAmals = [
            { id: 1, name: 'نماز جعفر طیار', count: 0, order_num: 1 },
            { id: 2, name: 'نماز شب', count: 0, order_num: 2 },
            { id: 3, name: 'نماز استغفار', count: 0, order_num: 3 }
          ];
          const normalizedDefaults = normalizeAmals(defaultAmals);
          setAmals(normalizedDefaults);
          localStorage.setItem('amals', JSON.stringify(normalizedDefaults));
          return;
        }

        // Successfully fetched from Supabase
        setAmals(next);
        // Also save to localStorage as backup
        localStorage.setItem('amals', JSON.stringify(next));
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
      // Fallback to localStorage
      const savedAmals = localStorage.getItem('amals');
      if (savedAmals) {
        try {
          const parsed = JSON.parse(savedAmals);
          const normalized = normalizeAmals(parsed);
          setAmals(normalized);
        } catch (e) {
          localStorage.removeItem('amals');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const saveAmals = async (updatedAmals) => {
    try {
      // Save to localStorage first for immediate UI update
      localStorage.setItem('amals', JSON.stringify(updatedAmals));
      setAmals(updatedAmals);
      setEditingAmal(null);
      
      // Try to sync with Supabase
      const errors = [];
      for (const amal of updatedAmals) {
        if (amal.id && amal.id > 100000) {
          // New amal (has timestamp ID), insert to database
          const { data, error } = await supabase
            .from('amals')
            .insert({
              name: amal.name,
              count: amal.count,
              order_num: amal.order_num || updatedAmals.indexOf(amal) + 1
            })
            .select()
            .single();
          
          if (error) {
            console.log('Insert error:', error);
            errors.push(error.message);
          } else if (data) {
            // Update the local ID with the database ID
            const finalAmals = updatedAmals.map(a => 
              a.id === amal.id ? { ...a, id: data.id } : a
            );
            localStorage.setItem('amals', JSON.stringify(finalAmals));
            setAmals(finalAmals);
          }
        } else {
          // Existing amal, update in database
          const { error } = await supabase
            .from('amals')
            .update({
              name: amal.name,
              count: amal.count,
              order_num: amal.order_num || updatedAmals.indexOf(amal) + 1
            })
            .eq('id', amal.id);
          
          if (error) {
            console.log('Update error:', error);
            errors.push(error.message);
          }
        }
      }

      if (errors.length > 0) {
        setError(`همگام‌سازی با دیتابیس انجام نشد: ${errors[0]}`);
      } else {
        setError(null);
        fetchAmals();
      }
      
      // Trigger storage event to notify main page
      const ts = Date.now();
      localStorage.setItem('amalsUpdated', ts);
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: 'amalsUpdated',
          newValue: String(ts)
        })
      );
      
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message);
    }
  };

  const updateAmal = (id, updates) => {
    const updatedAmals = state.amals.map(amal => 
      amal.id === id ? { ...amal, ...updates } : amal
    );
    saveAmals(updatedAmals);
  };

  const deleteAmal = async (id) => {
    if (!window.confirm('آیا از حذف این عمل مطمئن هستید؟')) {
      return;
    }
    
    try {
      // Delete from database if it's a real database ID
      if (id && id < 100000) {
        const { error } = await supabase
          .from('amals')
          .delete()
          .eq('id', id);
        
        if (error) {
          console.log('Delete error:', error);
          setError(`حذف از دیتابیس انجام نشد: ${error.message}`);
        }
      }
      
      // Update local state
      const updatedAmals = state.amals.filter(amal => amal.id !== id);
      localStorage.setItem('amals', JSON.stringify(updatedAmals));
      setAmals(updatedAmals);
      
      // Trigger storage event to notify main page
      const ts = Date.now();
      localStorage.setItem('amalsUpdated', ts);
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: 'amalsUpdated',
          newValue: String(ts)
        })
      );
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
    }
  };

  const incrementAmal = (id) => {
    const amal = state.amals.find(a => a.id === id);
    if (amal) {
      updateAmal(id, { count: amal.count + 1 });
    }
  };

  const resetAmal = (id) => {
    updateAmal(id, { count: 0 });
  };

  if (state.loading) {
    return (
      <div style={{ 
        width: '100vw', 
        height: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontFamily: 'AbarMidFaNum, Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>بارگذاری...</div>
          <div style={{ fontSize: '16px', opacity: '0.7' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100vw', 
      height: '100vh',
      backgroundColor: '#000000',
      position: 'relative',
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(20px)',
        padding: '20px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        zIndex: 100,
        textAlign: 'center'
      }}>
        <h1 style={{ 
          fontSize: '28px', 
          margin: '0',
          color: 'white',
          fontFamily: 'AbarMidFaNum, Arial, sans-serif',
          fontWeight: 'bold'
        }}>
          🙏 پنل مدیریت اعمال
        </h1>
        <p style={{ 
          fontSize: '14px', 
          margin: '5px 0 0 0',
          color: 'rgba(255,255,255,0.8)',
          fontFamily: 'AbarMidFaNum, Arial, sans-serif'
        }}>
          Amal Management Panel
        </p>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          marginTop: '10px',
          flexWrap: 'wrap'
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <GlassButton
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                background: 'rgba(255, 152, 0, 0.2)',
                border: '1px solid rgba(255, 152, 0, 0.5)',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif'
              }}
            >
              🏠 بازگشت به صفحه اصلی
            </GlassButton>
          </Link>
          <Link to="/admin" style={{ textDecoration: 'none' }}>
            <GlassButton
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                background: 'rgba(33, 150, 243, 0.2)',
                border: '1px solid rgba(33, 150, 243, 0.5)',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif'
              }}
            >
              📿 مدیریت ذکرها
            </GlassButton>
          </Link>
          <Link to="/quran-admin" style={{ textDecoration: 'none' }}>
            <GlassButton
              style={{
                padding: '8px 16px',
                fontSize: '12px',
                background: 'rgba(76, 175, 80, 0.2)',
                border: '1px solid rgba(76, 175, 80, 0.5)',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif'
              }}
            >
              📖 مدیریت قرآن
            </GlassButton>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Error Message */}
        {state.error && (
          <div style={{
            background: 'rgba(255, 0, 0, 0.1)',
            border: '1px solid rgba(255, 0, 0, 0.3)',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px',
            color: '#ff6b6b',
            textAlign: 'center',
            fontSize: '14px',
            fontFamily: 'AbarMidFaNum, Arial, sans-serif'
          }}>
            خطا: {state.error}
          </div>
        )}

        {/* Amals List */}
        <div style={{
          display: 'grid',
          gap: '15px'
        }}>
          {state.amals.map((amal) => (
            <div
              key={amal.id}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                borderRadius: '15px',
                padding: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'transform 0.3s ease'
              }}
            >
              {state.editingAmal === amal.id ? (
                // Edit Mode
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr auto auto',
                  gap: '10px',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    value={amal.name}
                    onChange={(e) => updateAmal(amal.id, { name: e.target.value })}
                    style={{
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  />
                  <input
                    type="number"
                    value={amal.count}
                    onChange={(e) => updateAmal(amal.id, { count: parseInt(e.target.value) || 0 })}
                    style={{
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      fontSize: '14px',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  />
                  <GlassButton
                    onClick={() => setEditingAmal(null)}
                    style={{
                      padding: '10px 15px',
                      fontSize: '12px',
                      background: 'rgba(33, 150, 243, 0.2)',
                      border: '1px solid rgba(33, 150, 243, 0.5)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  >
                    💾 ذخیره
                  </GlassButton>
                  <GlassButton
                    onClick={() => setEditingAmal(null)}
                    style={{
                      padding: '10px 15px',
                      fontSize: '12px',
                      background: 'rgba(255, 152, 0, 0.2)',
                      border: '1px solid rgba(255, 152, 0, 0.5)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  >
                    ❌ لغو
                  </GlassButton>
                </div>
              ) : (
                // View Mode
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr auto auto auto',
                  gap: '15px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    color: 'white',
                    fontSize: '16px',
                    fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                  }}>
                    {amal.name}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      color: '#00BFFF',
                      fontSize: '12px',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                      minWidth: '110px',
                      textAlign: 'center'
                    }}>
                      فعلی: {(Number.isFinite(parseInt(amal.count)) ? parseInt(amal.count) : 0).toLocaleString('fa-IR')}
                    </div>

                    <GlassButton
                      onClick={() => {
                        handleBulkIncrementAll(-1);
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: '14px',
                        background: 'rgba(255, 152, 0, 0.2)',
                        border: '1px solid rgba(255, 152, 0, 0.5)',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        minWidth: '30px',
                        borderRadius: '6px'
                      }}
                    >
                      -
                    </GlassButton>

                    <input
                      type="number"
                      value={(state.incrementInputs && state.incrementInputs[amal.id] !== undefined) ? state.incrementInputs[amal.id] : 0}
                      onChange={(e) => {
                        const delta = parseInt(e.target.value) || 0;
                        setState(prev => ({
                          ...prev,
                          incrementInputs: {
                            ...(prev.incrementInputs || {}),
                            [amal.id]: delta
                          }
                        }));
                      }}
                      style={{
                        width: '80px',
                        padding: '6px 8px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        fontSize: '14px',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        textAlign: 'center'
                      }}
                    />

                    <GlassButton
                      onClick={() => {
                        handleBulkIncrementAll(1);
                      }}
                      style={{
                        padding: '6px 10px',
                        fontSize: '14px',
                        background: 'rgba(76, 175, 80, 0.2)',
                        border: '1px solid rgba(76, 175, 80, 0.5)',
                        fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                        minWidth: '30px',
                        borderRadius: '6px'
                      }}
                    >
                      +
                    </GlassButton>
                  </div>
                  <GlassButton
                    onClick={() => setEditingAmal(amal.id)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      background: 'rgba(33, 150, 243, 0.2)',
                      border: '1px solid rgba(33, 150, 243, 0.5)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  >
                    ✏️
                  </GlassButton>
                  <GlassButton
                    onClick={() => deleteAmal(amal.id)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      background: 'rgba(244, 67, 54, 0.2)',
                      border: '1px solid rgba(244, 67, 54, 0.5)',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif'
                    }}
                  >
                    🗑️
                  </GlassButton>
                </div>
              )}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

export default AmalAdmin;
